import { cx } from '@/styles/muiTheme';

/** The MUI palette tone names a `Chip`'s `color` prop accepts. */
export type PaletteTone = 'default' | 'info' | 'success' | 'warning' | 'error';

/**
 * The raw hue each MUI palette tone resolves to.
 *
 * Exists because `StatusBadge` derives its fill, ring, dot and text from one
 * color with `alpha()`, which throws on keyword shorthands like `'default'`.
 * So every status map written against a `Chip` needs translating before it can
 * feed a badge.
 *
 * Keeping the two layers — status -> tone -> hue — rather than rewriting each
 * status map to hex is what makes those conversions provably color-neutral:
 * the tone maps stay untouched and only the shape changes.
 */
export const PALETTE_HUES: Record<PaletteTone, string> = {
  default: cx.text.muted,
  info: cx.blue.hover,
  success: cx.green,
  warning: cx.gold,
  error: cx.red.main,
};

/**
 * Hue for a palette tone, falling back to the neutral grey.
 *
 * Takes an optional tone so a caller can pass `SOME_MAP[status]` straight in
 * without repeating the `|| 'default'` guard at every call site — an unmapped
 * status is the common case, and handing `StatusBadge` an undefined color
 * throws rather than degrading.
 */
export const paletteHue = (tone?: PaletteTone) => PALETTE_HUES[tone ?? 'default'];
